/**
 * Media duration estimation utilities
 */

/**
 * Create a media element for duration detection
 */
function createMediaElement(file: File): {
  element: HTMLMediaElement;
  objectUrl: string;
} {
  const element = document.createElement(
    file.type.startsWith('video/') ? 'video' : 'audio'
  );
  element.preload = 'metadata';
  element.crossOrigin = 'anonymous';

  const objectUrl = URL.createObjectURL(file);
  element.src = objectUrl;

  return { element, objectUrl };
}

/**
 * Wait for media element metadata to load
 */
function waitForMetadata(element: HTMLMediaElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const handleLoaded = () => resolve();
    const handleError = () => reject(new Error('Failed to load media metadata.'));

    element.addEventListener('loadedmetadata', handleLoaded, { once: true });
    element.addEventListener('error', handleError, { once: true });
  });
}

/**
 * Clean up media element and object URL
 */
function cleanupMediaElement(element: HTMLMediaElement, objectUrl: string): void {
  element.pause();
  element.removeAttribute('src');
  element.load();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Get media duration in seconds using HTML5 Media API
 */
async function getMediaDurationSeconds(file: File): Promise<number | undefined> {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const { element, objectUrl } = createMediaElement(file);
  try {
    await waitForMetadata(element);
    return Number.isFinite(element.duration) ? element.duration : undefined;
  } finally {
    cleanupMediaElement(element, objectUrl);
  }
}

/**
 * Estimate media duration in seconds
 * This is a best-effort estimation that may fail for some file types
 */
export async function estimateMediaDuration(
  file: File
): Promise<number | undefined> {
  try {
    return await getMediaDurationSeconds(file);
  } catch {
    // Silently fail - duration estimation is optional
    return undefined;
  }
}
